import { useEffect, useRef, useState } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import CategoryPageLayout from '../../components/feature/CategoryPageLayout';
import CasinoCard from '../../components/feature/CasinoCard';
import CasinoReviewPanel from '../../components/feature/CasinoReviewPanel';
import { casinos } from '../../mocks/casinos';
import { getPlayLinkByBrand, trackRegisterClickByBrand } from '../../config/playLinks';

const bonusCasinoPresets = [
  { key: 'ipay9', label: 'iPay9', bonusRate: 0.5 },
  { key: 'spinaroo', label: 'Spinaroo', bonusRate: 0.8 },
  { key: 'me99', label: 'Me99', bonusRate: 1.0 },
  { key: 'bigpay77', label: 'BigPay77', bonusRate: 0.8 },
  { key: 'kingbet9', label: 'KingBet9', bonusRate: 1.0 },
  { key: 'gucci9', label: 'Gucci9', bonusRate: 0.5 },
];

const presetDepositAmounts = [20, 50, 100, 200];
const trustRatings = [
  { value: 5, suffix: '+', label: 'Years combined experience' },
  { value: 13, suffix: '+', label: 'Countries covered worldwide' },
  { value: 1800, suffix: '+', label: 'Hours of testing' },
  { value: 1, suffix: 'm+', label: 'Players per week' },
];

const faqItems = [
  {
    question: 'What is a casino payout percentage?',
    answer:
      'Payout percentages are determined by independent auditing companies to state the expected average rate of return to a player. A 95% payout rate indicates that for every $1 you gamble, you will win 0.95 back on average over many transactions.',
  },
  {
    question: 'How can I deposit money in online casinos?',
    answer:
      'Most reputable sites support cards, e-wallets, bank transfers, and local payment rails. Always verify fees, processing times, and supported currencies before depositing.',
  },
  {
    question: 'What is an initial deposit bonus?',
    answer:
      'An initial deposit bonus is a welcome promotion that adds extra value to your first deposit, usually as a match percentage. Always check wagering and withdrawal terms.',
  },
  {
    question: 'Is my money safe in a casino online?',
    answer:
      'Licensed casinos with SSL encryption, clear policies, and trusted payment partners are generally safer. We recommend checking regulation details before playing.',
  },
  {
    question: 'Can you win at online casinos?',
    answer:
      'Yes, but outcomes are still based on probability. Responsible bankroll management and game selection matter more than short-term luck.',
  },
  {
    question: 'What is the best game to play at an online casino to win money?',
    answer:
      'Games with higher RTP and lower house edge generally offer better long-term value. Choose formats that match your risk tolerance and budget.',
  },
  {
    question: 'Are online casinos rigged?',
    answer:
      'Legitimate casinos use audited RNG systems and licensed software. Unlicensed platforms are where players face the highest risk, so always verify credentials.',
  },
];

const getCasinoBrandKey = (name: string) =>
  name.toLowerCase().replace(/\sonline casino/g, '').replace(/[^a-z0-9]/g, '');

export default function BestForGames() {
  const [expandedReview, setExpandedReview] = useState<string | null>(null);
  const [selectedBonusCasino, setSelectedBonusCasino] = useState('me99');
  const [selectedDepositAmount, setSelectedDepositAmount] = useState<number>(50);
  const [customDepositInput, setCustomDepositInput] = useState('');
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const ratingsRef = useRef<HTMLElement | null>(null);
  const [animatedRatings, setAnimatedRatings] = useState<number[]>(trustRatings.map(() => 0));
  const [hasAnimatedRatings, setHasAnimatedRatings] = useState(false);

  const bestForGamesCasinos = casinos.filter(c => 
    c.tags?.includes('Best for Games') || c.tags?.includes('Best AUS')
  );

  const handleReviewClick = (casinoName: string) => {
    setExpandedReview(expandedReview === casinoName ? null : casinoName);
  };

  const activeBonusPreset =
    bonusCasinoPresets.find((item) => item.key === selectedBonusCasino) ?? bonusCasinoPresets[0];
  const freeMoney = Math.max(0, selectedDepositAmount * activeBonusPreset.bonusRate);
  const totalCredit = selectedDepositAmount + freeMoney;

  const formatCurrency = (value: number) =>
    `$${value.toLocaleString('en-AU', { maximumFractionDigits: 0 })}`;

  useEffect(() => {
    const sectionEl = ratingsRef.current;
    if (!sectionEl || hasAnimatedRatings) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry.isIntersecting) return;

        setHasAnimatedRatings(true);
        const startTime = performance.now();
        const durationMs = 1400;

        const tick = (now: number) => {
          const progress = Math.min((now - startTime) / durationMs, 1);
          const easedProgress = 1 - Math.pow(1 - progress, 3);

          setAnimatedRatings(trustRatings.map((item) => Math.round(item.value * easedProgress)));

          if (progress < 1) requestAnimationFrame(tick);
        };

        requestAnimationFrame(tick);
        observer.disconnect();
      },
      { threshold: 0.35 }
    );

    observer.observe(sectionEl);
    return () => observer.disconnect();
  }, [hasAnimatedRatings]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <CategoryPageLayout
        title="Best Online Casinos for Games 2026"
        subtitle="Expert-reviewed casinos with bigger game libraries and better variety"
        description="We test and rank casinos based on game depth, software quality, and category balance across slots, live dealer, table games, and jackpot titles. Use this guide to find a platform that matches your play style with strong content and reliable performance."
        lastUpdated="Feb 15, 2026"
        heroVariant="editorial"
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Best for Games', href: '/best-for-games' }
        ]}
      >
        {/* Game Category Icons Banner */}
        <div className="bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-200 rounded-xl p-4 sm:p-6 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 sm:gap-6">
            <div>
              <h3 className="text-xl sm:text-lg font-bold text-gray-900 mb-2">Game Variety Matters</h3>
              <p className="text-sm text-gray-600">Top casinos offer 2,000+ games from leading providers</p>
            </div>
            <div className="grid grid-cols-2 sm:flex sm:items-center gap-3 sm:gap-6">
              <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-white/70 bg-white/60 px-4 py-3 sm:rounded-none sm:border-0 sm:bg-transparent sm:px-0 sm:py-0">
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-lg shadow-sm">
                  <i className="ri-trophy-line text-2xl text-orange-600"></i>
                </div>
                <span className="text-xs font-medium text-gray-700">Slots</span>
              </div>
              <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-white/70 bg-white/60 px-4 py-3 sm:rounded-none sm:border-0 sm:bg-transparent sm:px-0 sm:py-0">
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-lg shadow-sm">
                  <i className="ri-live-line text-2xl text-orange-600"></i>
                </div>
                <span className="text-xs font-medium text-gray-700">Live</span>
              </div>
              <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-white/70 bg-white/60 px-4 py-3 sm:rounded-none sm:border-0 sm:bg-transparent sm:px-0 sm:py-0">
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-lg shadow-sm">
                  <i className="ri-layout-grid-line text-2xl text-orange-600"></i>
                </div>
                <span className="text-xs font-medium text-gray-700">Table</span>
              </div>
              <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-white/70 bg-white/60 px-4 py-3 sm:rounded-none sm:border-0 sm:bg-transparent sm:px-0 sm:py-0">
                <div className="w-12 h-12 flex items-center justify-center bg-white rounded-lg shadow-sm">
                  <i className="ri-money-dollar-circle-line text-2xl text-orange-600"></i>
                </div>
                <span className="text-xs font-medium text-gray-700">Jackpots</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Casino List */}
          <div className="lg:col-span-2 space-y-6">
            {bestForGamesCasinos.map((casino) => (
              <div key={casino.name}>
                {/* Enhanced Casino Card with Game Stats */}
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6">
                      {/* Rank Badge */}
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-orange-500 to-orange-600 text-white font-bold text-xl rounded-lg shadow-md">
                          {casino.rank}
                        </div>
                      </div>

                      {/* Casino Logo */}
                      <div className="flex-shrink-0 w-full sm:w-28 h-40 sm:h-28 bg-white rounded-lg border border-gray-200 flex items-center justify-center overflow-hidden">
                        <img
                          src={casino.logo}
                          alt={casino.name}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Casino Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                          <div>
                            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">{casino.name}</h3>
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-1">
                                {[...Array(5)].map((_, i) => (
                                  <i
                                    key={i}
                                    className={`${
                                      i < Math.floor(casino.rating)
                                        ? 'ri-star-fill text-yellow-500'
                                        : 'ri-star-line text-gray-300'
                                    } text-sm`}
                                  ></i>
                                ))}
                              </div>
                              <span className="text-sm font-semibold text-gray-900">{casino.rating}</span>
                            </div>
                          </div>
                        </div>

                        <p className="text-gray-600 text-sm mb-4">{casino.description}</p>

                        {/* Game Library Stats */}
                        <div className="bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-4 mb-4">
                          <div className="flex items-center gap-2 mb-3">
                            <i className="ri-gamepad-line text-orange-600"></i>
                            <span className="text-xs font-semibold text-orange-900 uppercase">Game Library</span>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div>
                              <div className="text-xs text-gray-600 mb-1">Total Games</div>
                              <div className="text-base font-bold text-orange-900">{casino.gameLibrary?.totalGames || casino.games}</div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-600 mb-1">Providers</div>
                              <div className="text-base font-bold text-orange-900">{casino.gameLibrary?.providers || '50+'}</div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-600 mb-1">Exclusives</div>
                              <div className="text-base font-bold text-orange-900">{casino.gameLibrary?.exclusives || '20+'}</div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-600 mb-1">Live Tables</div>
                              <div className="text-base font-bold text-orange-900">{casino.gameLibrary?.liveTables || '100+'}</div>
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row items-stretch gap-3">
                          <a
                            href={getPlayLinkByBrand(getCasinoBrandKey(casino.name))}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => trackRegisterClickByBrand(getCasinoBrandKey(casino.name))}
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3.5 px-6 min-h-[44px] rounded-lg transition-colors text-center whitespace-nowrap"
                          >
                            Visit Casino
                          </a>
                          <button
                            onClick={() => handleReviewClick(casino.name)}
                            className="flex-1 bg-white hover:bg-gray-50 text-gray-900 font-semibold py-3.5 px-6 min-h-[44px] rounded-lg border-2 border-gray-300 transition-colors whitespace-nowrap"
                          >
                            {expandedReview === casino.name ? 'Hide Review' : 'Read Review'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expandable Review Panel */}
                {expandedReview === casino.name && (
                  <div className="mt-4">
                    <CasinoReviewPanel
                      casino={casino}
                      isOpen={expandedReview === casino.name}
                      onClose={() => setExpandedReview(null)}
                    />
                  </div>
                )}
              </div>
            ))}

            {/* Bonus Guide & Calculator */}
            <section className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-4">How to make the most of bonuses</h2>
                <p className="text-gray-700 leading-relaxed mb-5">
                  New casino players will receive a bonus when they sign-up for a casino for real money.
                  Casinos usually give out bonuses in the form of deposit matches where a certain
                  percentage of your deposit is matched, so the bigger your deposit, the bigger your
                  bonus.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Check each online casino&apos;s wagering requirements before you commit. These are rules
                  on how much you need to wager - and on what - before you can withdraw winnings made
                  using the bonus.
                </p>
              </div>

              <div className="rounded-xl bg-green-50 border border-green-100 px-6 py-5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center flex-shrink-0">
                    <i className="ri-lightbulb-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Editor&apos;s tip</h3>
                    <p className="text-gray-700 leading-relaxed">
                      I recommend comparing different offers to find the bonus that best suits your playing style — whether it’s a deposit match, free spins, or a cashback deal.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                <div className="grid grid-cols-1 lg:grid-cols-2">
                  <div className="p-6 border-b lg:border-b-0 lg:border-r border-gray-200">
                    <h3 className="text-2xl font-bold text-gray-900 mb-6">Bonus calculator</h3>
                    <p className="text-gray-700 mb-3">Choose a casino:</p>
                    <div className="grid grid-cols-3 gap-3 mb-6">
                      {bonusCasinoPresets.map((item) => (
                        <button
                          key={item.key}
                          onClick={() => setSelectedBonusCasino(item.key)}
                          className={`h-16 rounded-xl border text-sm font-semibold transition-colors ${
                            selectedBonusCasino === item.key
                              ? 'border-purple-500 text-purple-700 bg-purple-50'
                              : 'border-gray-200 text-gray-700 bg-white hover:border-gray-300'
                          }`}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>

                    <p className="text-gray-700 mb-3">How much do you want to deposit?</p>
                    <div className="grid grid-cols-3 gap-3">
                      {presetDepositAmounts.map((amount) => (
                        <button
                          key={amount}
                          onClick={() => {
                            setSelectedDepositAmount(amount);
                            setCustomDepositInput('');
                          }}
                          className={`h-14 rounded-xl font-bold text-base transition-colors ${
                            selectedDepositAmount === amount && customDepositInput === ''
                              ? 'bg-purple-800 text-white'
                              : 'bg-purple-700 hover:bg-purple-800 text-white'
                          } transition-colors`}
                        >
                          {formatCurrency(amount)}
                        </button>
                      ))}
                      <input
                        type="number"
                        min={0}
                        value={customDepositInput}
                        onChange={(e) => {
                          const value = e.target.value;
                          setCustomDepositInput(value);
                          const parsed = Number(value);
                          if (!Number.isNaN(parsed) && parsed >= 0) {
                            setSelectedDepositAmount(parsed);
                          }
                        }}
                        placeholder="Custom"
                        className="h-14 rounded-xl border border-gray-300 bg-gray-100 text-gray-700 font-semibold text-base px-4 outline-none focus:ring-2 focus:ring-purple-300"
                      />
                    </div>
                  </div>

                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-6">
                      Deposit <span className="text-purple-700">{formatCurrency(selectedDepositAmount)}</span>{' '}
                      get <span className="text-green-600">{formatCurrency(freeMoney)}</span> as free money
                    </h3>
                    <p className="text-gray-700 mb-3">Bonus info:</p>

                    <div className="grid grid-cols-2 rounded-xl border border-gray-200 overflow-hidden mb-8">
                      <div className="bg-gray-50 p-4 text-center border-r border-gray-200">
                        <div className="text-sm text-gray-500 mb-1">Free money</div>
                        <div className="text-2xl font-bold text-gray-900">{formatCurrency(freeMoney)}</div>
                      </div>
                      <div className="bg-purple-50 p-4 text-center">
                        <div className="text-sm text-gray-500 mb-1">Total credit</div>
                        <div className="text-2xl font-bold text-green-600">{formatCurrency(totalCredit)}</div>
                      </div>
                    </div>

                    <button className="w-full h-14 rounded-xl bg-orange-600 hover:bg-orange-700 text-white font-bold text-lg transition-colors">
                      Claim offer
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {/* Why play real money casino games */}
            <section className="mt-10">
              <h2 className="text-2xl mt-12 font-bold text-gray-900 mb-5">Why should you play real money casino games?</h2>
              <div className="flex items-start gap-3 mb-5">
                <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                  <i className="ri-user-3-line text-gray-600"></i>
                </div>
                <div>
                  <p className="font-bold text-gray-900">Lewis Crane - Content Editor</p>
                  <p className="text-gray-700 italic leading-relaxed">
                    &ldquo;Real money gaming unlocks the top casino bonuses, promotions, and jackpots, which
                    proves a big draw to players looking to maximize their gaming bankroll. However, there
                    are also some disadvantages to playing at real money online casinos. Here are some pros
                    and cons to help you make the best decision.&rdquo;
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Pros</h3>
                  <div className="space-y-4">
                    {[
                      { title: 'Jackpots', desc: 'Offer the chance to claim real money winnings' },
                      { title: 'Games', desc: 'Play a wider range of online casino games' },
                      { title: 'Bonuses', desc: 'Access lucrative bonuses to boost your bankroll' },
                      { title: 'VIP programs', desc: 'Loyalty programs reward frequent players' },
                    ].map((item) => (
                      <div key={item.title} className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-green-100 text-green-700 flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-line"></i>
                        </div>
                        <p className="text-gray-800 leading-relaxed">
                          <span className="font-bold">{item.title}:</span> {item.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Cons</h3>
                  <div className="space-y-4">
                    {[
                      { title: 'Risk to your bankroll', desc: 'You put your own money at risk' },
                      { title: 'Payout speed', desc: 'Some sites have lengthy withdrawal processing times' },
                    ].map((item) => (
                      <div key={item.title} className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0">
                          <i className="ri-close-line"></i>
                        </div>
                        <p className="text-gray-800 leading-relaxed">
                          <span className="font-bold">{item.title}:</span> {item.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Key terms */}
            <section className="mt-14">
              <h2 className="text-3xl mt-12 font-bold text-gray-900 mb-5">Key terms for real money casino games</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Playing <a href="#" className="text-purple-700 underline">online casino games</a> for real money provides
                entertainment and the opportunity to win cash. However, you might come across some unfamiliar
                terms when you&apos;re deciding which games to play.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Take a look at the list below to ensure you know exactly what each one means:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  'Return-to-player percentage (RTP): Games with higher RTPs give you better odds of winning and potentially higher returns over time.',
                  'Minimum bets: Considering minimum bets is important to manage a smaller bankroll effectively and play within your budget.',
                  'House edge: A lower house edge means an increased chance of winning and potentially more favorable outcomes as you play.',
                  'Maximum bets: Understanding maximum bets can help you decide whether a game aligns with your desired level of risk and potential rewards.',
                  'Volatility: Understanding the volatility of a game helps you gauge risk level and choose games that match your preferences for big wins or more frequent but smaller payouts.',
                  'Jackpot: Games with fixed or progressive jackpots offer thrilling opportunities to collect significant winnings and add excitement your experience.',
                ].map((item) => {
                  const [title, ...rest] = item.split(':');
                  return (
                    <div key={title} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line"></i>
                      </div>
                      <p className="text-gray-800 leading-relaxed">
                        <span className="font-bold">{title}:</span>{rest.join(':')}
                      </p>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Jackpot slots */}
            <section className="mt-14">
              <h2 className="text-3xl mt-12 font-bold text-gray-900 mb-5">What about jackpot slots?</h2>
              <p className="text-gray-700 leading-relaxed mb-5">
                Jackpot slots at real money online casinos offer you the chance to win huge, prizes without
                needing to wager very much cash. There are two types of jackpots to look out for:
              </p>
              <div className="space-y-4 mb-6">
                {[
                  'Fixed jackpots: You’ll know exactly how much money you can win before spinning the reels of the online slot.',
                  'Progressive jackpots: The prize money keeps increasing as players make bets until someone wins the jackpot.',
                ].map((item) => {
                  const [title, ...rest] = item.split(':');
                  return (
                    <div key={title} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line"></i>
                      </div>
                      <p className="text-gray-800 leading-relaxed">
                        <span className="font-bold">{title}:</span>{rest.join(':')}
                      </p>
                    </div>
                  );
                })}
              </div>
              <p className="text-gray-700 leading-relaxed mb-4">
                Fixed and <a href="#" className="text-purple-700 underline">progressive jackpot slots</a> are available at all of our
                recommended online casinos with real money games. If you&apos;re looking for more frequent wins,
                we advise sticking to standard real money slots due to their favorable return-to-player
                percentages (RTP).
              </p>
              <p className="text-gray-700 leading-relaxed">
                Jackpot slots tend to pay out slightly less often than normal slots to compensate for the
                substantial prize you can win while enjoying them. But that&apos;s not to say it&apos;s not worth
                having a dabble on progressive jackpot slots if you&apos;re in the mood to chase that unlikely
                long shot.
              </p>
            </section>

            {/* Safety section */}
            <section className="mt-14">
              <h2 className="text-3xl mt-12 font-bold text-gray-900 mb-5">Safety - how can you tell a gambling site is legit?</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                With so many real money online casinos out there, distinguishing between trustworthy
                platforms and potential risks is crucial. Explore the key factors below to understand what
                to look for in a legit online casino and ensure your experience is as safe, fair and
                reliable as possible.
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  'Valid license: A hallmark of a reputable online casino is a valid license from well-known gaming authorities such as the Curacao Gaming Authority (CGA) or the PAGCOR, ensuring fair play and accountability.',
                  'Swift payouts: Legitimate casinos offer a range of banking methods for deposits and withdrawals, and they process payouts promptly. Anything under 48 hours is generally considered on the good, reliable side.',
                  'Security measures: Look for SSL encryption, like that used by top banks, to secure your personal and financial information. 2FA and an extensive KYC protocol help protect both players and casinos from bad actors.',
                  'Customer support: Reliable customer support is crucial. Real online casino sites provide 24/7 assistance via live chat, email, or phone.',
                  'Background check: Verify the casino\'s registration details and check where it operates.',
                  'Clear terms and conditions: Legitimate casinos should have transparent terms and conditions, clearly outlining rules and policies.',
                  'Game fairness: Reputable sites feature online casino games for real money from trusted software providers, ensuring fairness.',
                  'Responsible gambling tools: Reputable casinos prioritize responsible gambling, offering a suite of tools to help players manage their gaming activity.',
                  'Transparent reviews: Explore reviews on platforms like Trustpilot or Casino.org to gauge the experiences of other players.',
                  'Secure payment options: Look for casinos supporting trusted payment methods like credit cards and e-wallets.',
                ].map((item) => {
                  const [title, ...rest] = item.split(':');
                  return (
                    <div key={title} className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line"></i>
                      </div>
                      <p className="text-gray-800 leading-relaxed">
                        <span className="font-bold">{title}:</span>{rest.join(':')}
                      </p>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Responsible gambling */}
            <section className="mt-14">
              <h2 className="text-3xl mt-12 font-bold text-gray-900 mb-5">Responsible gambling</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                People gamble for plenty of reasons. Some for entertainment, some for the thrill of winning,
                and some for the social aspect. Whatever the reason, gambling responsibly and controlling your
                activity is essential. Here are a few tips when it comes to real online gambling:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  'Don’t think of gambling as a way to make money. The casino will always have an edge over the player.',
                  'Never gamble while under the influence. Alcohol can cloud your judgment and easily let bad habits get out of control.',
                  'Set a spending limit and then stick to it.',
                  'Take frequent breaks. It’s easy to lose track of time if you’re gambling on a loop.',
                  'Don’t chase your losses.',
                  'Don’t gamble when you’re angry, upset, or stressed.',
                ].map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line"></i>
                    </div>
                    <p className="text-gray-800 leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Ratings */}
            <section ref={ratingsRef} className="mt-16">
              <h2 className="text-3xl mt-12 font-bold text-gray-900 text-center mb-8">Ratings you can trust</h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {trustRatings.map((item, index) => (
                  <div
                    key={item.label}
                    className="bg-white border border-gray-200 rounded-xl p-6 text-center shadow-sm"
                  >
                    <div className="text-4xl font-bold text-purple-800 mb-2">
                      {animatedRatings[index]}
                      {item.suffix}
                    </div>
                    <div className="text-sm text-gray-600 leading-tight">{item.label}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQs */}
            <section className="mt-16">
              <h2 className="text-3xl mt-12 font-bold text-gray-900 mb-4">FAQs</h2>
              <div className="border-t border-gray-200">
                {faqItems.map((item, index) => {
                  const isOpen = openFaqIndex === index;
                  return (
                    <div key={item.question} className="border-b border-gray-200">
                      <button
                        onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                        className="w-full py-4 flex items-center justify-between text-left"
                      >
                        <h3 className="text-lg leading-snug font-semibold text-gray-900">{item.question}</h3>
                        <i
                          className={`${
                            isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'
                          } text-2xl text-gray-700`}
                        ></i>
                      </button>
                      {isOpen && <p className="pb-4 text-base text-gray-700 leading-relaxed">{item.answer}</p>}
                    </div>
                  );
                })}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Game Selection Guide */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center bg-orange-100 rounded-lg">
                  <i className="ri-lightbulb-line text-orange-600 text-xl"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">Game Selection Guide</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                We evaluate casinos based on game variety, quality, and provider reputation to help you find the best gaming experience.
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 text-sm mt-0.5 flex-shrink-0"></i>
                  <span className="text-sm text-gray-700">2,000+ games minimum</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 text-sm mt-0.5 flex-shrink-0"></i>
                  <span className="text-sm text-gray-700">Top-tier software providers</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 text-sm mt-0.5 flex-shrink-0"></i>
                  <span className="text-sm text-gray-700">Regular new releases</span>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 text-sm mt-0.5 flex-shrink-0"></i>
                  <span className="text-sm text-gray-700">Exclusive titles available</span>
                </div>
              </div>
            </div>

            {/* Game Categories Stats */}
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Average Game Counts</h3>
              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-700">Slots</span>
                    <span className="text-sm font-semibold text-gray-900">2,500+</span>
                  </div>
                  <div className="h-2 bg-white rounded-full overflow-hidden">
                    <div className="h-full bg-orange-500 rounded-full" style={{ width: '85%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-700">Live Dealer</span>
                    <span className="text-sm font-semibold text-gray-900">150+</span>
                  </div>
                  <div className="h-2 bg-white rounded-full overflow-hidden">
                    <div className="h-full bg-orange-500 rounded-full" style={{ width: '60%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-700">Table Games</span>
                    <span className="text-sm font-semibold text-gray-900">200+</span>
                  </div>
                  <div className="h-2 bg-white rounded-full overflow-hidden">
                    <div className="h-full bg-orange-500 rounded-full" style={{ width: '70%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-700">Jackpots</span>
                    <span className="text-sm font-semibold text-gray-900">50+</span>
                  </div>
                  <div className="h-2 bg-white rounded-full overflow-hidden">
                    <div className="h-full bg-orange-500 rounded-full" style={{ width: '40%' }}></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Top Providers */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Top Software Providers</h3>
              <div className="space-y-2">
                {['JILI', 'Bng', 'VPower', 'Microgaming', 'VPlus', 'Pragmatic Play', 'Pegasus', 'ACE333', 'CQ9', 'Rich Gaming', 'WF Gaming', 'Xe88', 'Fa Chai', 'Epic Win', 'YGR'].map((provider, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                    <i className="ri-star-fill text-yellow-500 text-xs"></i>
                    <span>{provider}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Methodology Section */}
        <div className="mt-12 bg-gradient-to-r from-orange-600 to-orange-700 rounded-xl p-8 text-white">
          <div className="max-w-7xl">
            <h2 className="text-2xl font-bold mb-4">How We Evaluate Game Libraries</h2>
            <p className="text-orange-100 mb-6">
              Our team of gaming experts thoroughly tests each casino's game selection to ensure quality, variety, and fair play. We consider multiple factors to determine which casinos offer the best gaming experience.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <i className="ri-check-double-line text-2xl flex-shrink-0"></i>
                <div>
                  <div className="font-semibold mb-1">Game Variety Testing</div>
                  <div className="text-sm text-orange-100">We catalog and test games across all categories</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-double-line text-2xl flex-shrink-0"></i>
                <div>
                  <div className="font-semibold mb-1">Provider Verification</div>
                  <div className="text-sm text-orange-100">Only licensed and reputable software providers</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-double-line text-2xl flex-shrink-0"></i>
                <div>
                  <div className="font-semibold mb-1">RTP Analysis</div>
                  <div className="text-sm text-orange-100">We verify return-to-player percentages</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-check-double-line text-2xl flex-shrink-0"></i>
                <div>
                  <div className="font-semibold mb-1">Update Frequency</div>
                  <div className="text-sm text-orange-100">Regular new game releases and updates</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CategoryPageLayout>

      <Footer />
    </div>
  );
}